import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  getLastNDaysDateRange,
  getPreviousPeriodDateRange,
  getPreviousYearDateRange,
} from 'src/utils/dateUtils';
import { format, differenceInDays, parseISO } from 'date-fns';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  Autocomplete,
  Checkbox,
  Chip,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { useQuery } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import palette from 'src/theme/palette';
import { KIPIFY_FILTER_APPLY_BUTTON_CLASS } from 'src/theme/kipifyUi';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import {
  getLookbackCompareAutoDisabledMessage,
  getLookbackCompareBlockedMessage,
  getLookbackCustomRangeBlockedMessage,
  getLookbackPlanLimitCaption,
  getLookbackPresetBlockedMessage,
} from 'src/utils/lookbackMessages';
import {
  GET_INVENTORY_DATA,
  GET_SALES_TIME_SERIES,
} from 'src/utils/gql/inventoryQueries';
import { FILTER_COUNTRY_OPTIONS } from 'src/constants/countryOptions';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'visible',
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
    maxWidth: 'calc(100% - 240px)',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    marginTop: '60px',
    maxWidth: '100%',
  },
}));

const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '16px',
  boxSizing: 'border-box',
  overflow: 'visible',
  minHeight: '100%',
}));

const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: palette.common.white,
    '& fieldset': { borderColor: palette.primary.main },
  },
};

const chartLoadPulseKeyframes = keyframes({
  '0%, 100%': { opacity: 0.4, transform: 'scaleY(0.7)' },
  '50%': { opacity: 1, transform: 'scaleY(1)' },
});

/** Gerarchia grafico: main = periodo principale (linee piene, in evidenza); compare = periodo di confronto (linee secondarie, stessi colori ma tenui e tratteggiate). */
const CHART_MAIN = {
  strokeWidth: 2,
  dotRadius: 2,
  activeDotRadius: 4,
} as const;
const CHART_COMPARE = {
  strokeWidth: 1.25,
  strokeDasharray: '5 4',
  dot: false as const,
  activeDotRadius: 3,
  /** Verde desaturato: stessa famiglia del primary, non compete con main. */
  revenueStroke: '#7eb89e',
  /** Grigio chiaro: stessa famiglia quantity, ruolo secondario. */
  quantityStroke: palette.grey[400],
} as const;

interface ChartBucket {
  label: string;
  revenue: number;
  quantity: number;
  /** Compare period (quando Compare ≠ None) */
  revenueCompare?: number;
  quantityCompare?: number;
}

type SalesTimeSeriesPoint = {
  date: string;
  collectionTitle?: string | null;
  revenue: number;
  quantity: number;
};

const TOOLTIP_ORDER = [
  'revenue',
  'revenueCompare',
  'quantity',
  'quantityCompare',
] as const;
const TOOLTIP_LABELS: Record<string, string> = {
  revenue: 'Revenue (main)',
  revenueCompare: 'Revenue (compare)',
  quantity: 'Quantity (main)',
  quantityCompare: 'Quantity (compare)',
};
const TOOLTIP_COLORS: Record<string, string> = {
  revenue: palette.primary.main,
  revenueCompare: CHART_COMPARE.revenueStroke,
  quantity: palette.grey[600],
  quantityCompare: CHART_COMPARE.quantityStroke,
};

const CategoryAnalysis: React.FC = () => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { lookbackDays } = useTrialStatus();
  const containerSx = sidebarCollapsed
    ? {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '72px',
          width: 'calc(100% - 72px)',
          maxWidth: 'calc(100% - 72px)',
        },
      }
    : undefined;

  const [dateRange, setDateRange] = useState(() => getLastNDaysDateRange(30));
  const [dateRangePreset, setDateRangePreset] = useState<string>('30');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [compareMode, setCompareMode] = useState<string>('none');
  const [compareDateRange, setCompareDateRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [showCustomCompareDates, setShowCustomCompareDates] = useState(false);
  const [tempCustomCompareDates, setTempCustomCompareDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [collectionFilter, setCollectionFilter] = useState<string[]>([]);
  const [vendorFilter, setVendorFilter] = useState<string[]>([]);
  const [productTypeFilter, setProductTypeFilter] = useState<string[]>([]);
  /** Categorie da mostrare nel grafico (collections). Vuoto = tutte. */
  const [chartCategoryFilter, setChartCategoryFilter] = useState<string[]>([]);

  const activeFilterCount =
    countryFilter.length +
    (customerTypeFilter ? 1 : 0) +
    tagFilter.length +
    collectionFilter.length +
    vendorFilter.length +
    productTypeFilter.length;

  useEffect(() => {
    if (compareMode === 'previous_period') {
      setCompareDateRange(getPreviousPeriodDateRange(dateRange));
    } else if (compareMode === 'previous_year') {
      setCompareDateRange(getPreviousYearDateRange(dateRange));
    }
  }, [dateRange, compareMode]);

  const lookbackMinDate = useMemo(() => {
    if (lookbackDays === null || lookbackDays === undefined) return null;
    const min = new Date();
    min.setDate(min.getDate() - lookbackDays);
    return min.toISOString().split('T')[0];
  }, [lookbackDays]);

  const isRangeWithinLookback = useCallback(
    (range: { from: string; to: string } | null | undefined) => {
      if (!range || !lookbackMinDate) return true;
      return range.from >= lookbackMinDate;
    },
    [lookbackMinDate],
  );

  const canUsePresetDays = useCallback(
    (days: number) => {
      if (lookbackDays === null || lookbackDays === undefined) return true;
      return days <= lookbackDays;
    },
    [lookbackDays],
  );

  const previousPeriodRangeCandidate = useMemo(
    () => getPreviousPeriodDateRange(dateRange),
    [dateRange],
  );
  const previousYearRangeCandidate = useMemo(
    () => getPreviousYearDateRange(dateRange),
    [dateRange],
  );
  const canUsePreviousPeriodCompare = useMemo(
    () => isRangeWithinLookback(previousPeriodRangeCandidate),
    [isRangeWithinLookback, previousPeriodRangeCandidate],
  );
  const canUsePreviousYearCompare = useMemo(
    () => isRangeWithinLookback(previousYearRangeCandidate),
    [isRangeWithinLookback, previousYearRangeCandidate],
  );

  useEffect(() => {
    if (compareMode === 'previous_period' && !canUsePreviousPeriodCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_period', lookbackDays),
        'warning',
      );
    } else if (compareMode === 'previous_year' && !canUsePreviousYearCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_year', lookbackDays),
        'warning',
      );
    }
  }, [
    compareMode,
    canUsePreviousPeriodCompare,
    canUsePreviousYearCompare,
    lookbackDays,
    snackbar,
  ]);

  const { data, loading, error } = useQuery(GET_INVENTORY_DATA, {
    variables: {
      dateRange: { from: dateRange.from, to: dateRange.to },
      ...(countryFilter?.length ? { countries: countryFilter } : {}),
      ...(customerTypeFilter ? { customerType: customerTypeFilter } : {}),
    },
    fetchPolicy: 'network-only',
  });

  const { data: timeSeriesData, loading: timeSeriesLoading } = useQuery(
    GET_SALES_TIME_SERIES,
    {
      variables: {
        dateRange: { from: dateRange.from, to: dateRange.to },
        ...(countryFilter?.length ? { countries: countryFilter } : {}),
        ...(customerTypeFilter ? { customerType: customerTypeFilter } : {}),
        ...(chartCategoryFilter?.length
          ? { collectionTitles: chartCategoryFilter }
          : {}),
        groupByCollection:
          chartCategoryFilter.length >= 2 && chartCategoryFilter.length <= 5,
      },
      fetchPolicy: 'network-only',
    },
  );

  const compareDateRangeEffective =
    compareMode !== 'none' && compareDateRange?.from && compareDateRange?.to
      ? compareDateRange
      : null;

  const { data: compareTimeSeriesData } = useQuery(GET_SALES_TIME_SERIES, {
    variables: {
      dateRange: {
        from: compareDateRangeEffective?.from ?? dateRange.from,
        to: compareDateRangeEffective?.to ?? dateRange.to,
      },
      ...(countryFilter?.length ? { countries: countryFilter } : {}),
      ...(customerTypeFilter ? { customerType: customerTypeFilter } : {}),
      ...(chartCategoryFilter?.length
        ? { collectionTitles: chartCategoryFilter }
        : {}),
      groupByCollection:
        chartCategoryFilter.length >= 2 && chartCategoryFilter.length <= 5,
    },
    fetchPolicy: 'network-only',
    skip: !compareDateRangeEffective,
  });

  const inventoryData = data?.getInventoryData ?? [];
  const rawTimeSeries = timeSeriesData?.getSalesTimeSeries ?? [];
  const rawCompareTimeSeries = compareTimeSeriesData?.getSalesTimeSeries ?? [];

  const isMultiCategoryOverlayMode =
    chartCategoryFilter.length >= 2 && chartCategoryFilter.length <= 5;

  const buildBucketsFromSeries = (
    series: SalesTimeSeriesPoint[],
    compare: SalesTimeSeriesPoint[],
  ): ChartBucket[] => {
    const hasCompare = compareDateRangeEffective && compare.length > 0;
    return series.map((p, i) => {
      const d = parseISO(p.date);
      const from = parseISO(dateRange.from);
      const to = parseISO(dateRange.to);
      const days = differenceInDays(to, from) + 1;
      const label = days >= 60 ? format(d, 'MMM yyyy') : format(d, 'dd MMM');
      const cmp = hasCompare ? compare[i] : null;
      return {
        label,
        revenue: Math.round(p.revenue * 100) / 100,
        quantity: Math.round(p.quantity * 10) / 10,
        ...(hasCompare && cmp
          ? {
              revenueCompare: Math.round(Number(cmp.revenue) * 100) / 100,
              quantityCompare: Math.round(Number(cmp.quantity) * 10) / 10,
            }
          : {}),
      };
    });
  };

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    inventoryData.forEach((p: { tags?: string[] }) =>
      p.tags?.forEach((t) => set.add(t)),
    );
    return Array.from(set).sort();
  }, [inventoryData]);

  const availableCollections = useMemo(() => {
    const set = new Set<string>();
    inventoryData.forEach((p: { collections?: string[] }) =>
      p.collections?.forEach((c) => set.add(c)),
    );
    return Array.from(set).sort();
  }, [inventoryData]);

  const availableVendors = useMemo(() => {
    const set = new Set<string>();
    inventoryData.forEach((p: { vendor?: string }) => {
      if (p.vendor) set.add(p.vendor);
    });
    return Array.from(set).sort();
  }, [inventoryData]);

  const availableProductTypes = useMemo(() => {
    const set = new Set<string>();
    inventoryData.forEach((p: { productType?: string }) => {
      if (p.productType) set.add(p.productType);
    });
    return Array.from(set).sort();
  }, [inventoryData]);

  const filteredData = useMemo(() => {
    let filtered = inventoryData;
    if (tagFilter.length > 0) {
      filtered = filtered.filter((p: { tags?: string[] }) =>
        p.tags?.some((t) => tagFilter.includes(t)),
      );
    }
    if (collectionFilter.length > 0) {
      filtered = filtered.filter((p: { collections?: string[] }) =>
        p.collections?.some((c) => collectionFilter.includes(c)),
      );
    }
    if (vendorFilter.length > 0) {
      filtered = filtered.filter(
        (p: { vendor?: string }) => p.vendor && vendorFilter.includes(p.vendor),
      );
    }
    if (productTypeFilter.length > 0) {
      filtered = filtered.filter(
        (p: { productType?: string }) =>
          p.productType && productTypeFilter.includes(p.productType),
      );
    }
    return filtered;
  }, [
    inventoryData,
    tagFilter,
    collectionFilter,
    vendorFilter,
    productTypeFilter,
  ]);

  /** Prodotti da includere nel grafico: filtro per categoria (chartCategoryFilter = collections) */
  const chartDataProducts = useMemo(() => {
    if (chartCategoryFilter.length === 0) return filteredData;
    return filteredData.filter((p: { collections?: string[] }) =>
      p.collections?.some((c) => chartCategoryFilter.includes(c)),
    );
  }, [filteredData, chartCategoryFilter]);

  /** Buckets dal backend: serie temporali reali; se compare attivo, allineati per indice con periodo di confronto. */
  const chartBuckets = useMemo((): ChartBucket[] => {
    if (isMultiCategoryOverlayMode) return [];
    return buildBucketsFromSeries(
      rawTimeSeries as SalesTimeSeriesPoint[],
      rawCompareTimeSeries as SalesTimeSeriesPoint[],
    );
  }, [
    rawTimeSeries,
    rawCompareTimeSeries,
    compareDateRangeEffective,
    dateRange.from,
    dateRange.to,
    isMultiCategoryOverlayMode,
  ]);

  type MultiCategoryBucket = { label: string } & Record<string, number>;

  const multiCategorySeries = useMemo(() => {
    if (!isMultiCategoryOverlayMode) {
      return {
        buckets: [] as MultiCategoryBucket[],
        keys: [] as Array<{
          mainKey: string;
          compareKey: string;
          label: string;
        }>,
      };
    }

    const selected = chartCategoryFilter.slice(0, 5);
    const keys = selected.map((c, i) => ({
      label: c,
      mainKey: `rev_${i}`,
      compareKey: `revCmp_${i}`,
    }));
    const keyByCategory = new Map<
      string,
      { mainKey: string; compareKey: string }
    >();
    keys.forEach((k) =>
      keyByCategory.set(k.label, {
        mainKey: k.mainKey,
        compareKey: k.compareKey,
      }),
    );

    const base = rawTimeSeries as SalesTimeSeriesPoint[];
    const cmp = rawCompareTimeSeries as SalesTimeSeriesPoint[];

    // Raggruppa per categoria mantenendo l'ordine per data (già ORDER BY date, collectionTitle lato backend).
    const byCat = new Map<string, SalesTimeSeriesPoint[]>();
    const byCatCmp = new Map<string, SalesTimeSeriesPoint[]>();
    for (const p of base) {
      const c = String(p.collectionTitle || '').trim();
      if (!c || !keyByCategory.has(c)) continue;
      const arr = byCat.get(c) ?? [];
      arr.push(p);
      byCat.set(c, arr);
    }
    for (const p of cmp) {
      const c = String(p.collectionTitle || '').trim();
      if (!c || !keyByCategory.has(c)) continue;
      const arr = byCatCmp.get(c) ?? [];
      arr.push(p);
      byCatCmp.set(c, arr);
    }

    // Unisci su una tabella wide: la label è SEMPRE quella del periodo main.
    // Il compare viene allineato per indice (come nel grafico singolo esistente).
    const rowsByLabel = new Map<string, MultiCategoryBucket>();
    for (const k of keys) {
      const mainSeries = byCat.get(k.label) ?? [];
      const cmpSeries = byCatCmp.get(k.label) ?? [];
      for (let i = 0; i < mainSeries.length; i++) {
        const p = mainSeries[i];
        const d = parseISO(p.date);
        const from = parseISO(dateRange.from);
        const to = parseISO(dateRange.to);
        const days = differenceInDays(to, from) + 1;
        const label = days >= 60 ? format(d, 'MMM yyyy') : format(d, 'dd MMM');

        const row =
          rowsByLabel.get(label) || ({ label } as MultiCategoryBucket);
        row[k.mainKey] = Number(p.revenue || 0);
        const cmpPoint = cmpSeries[i];
        if (compareDateRangeEffective && cmpPoint) {
          row[k.compareKey] = Number(cmpPoint.revenue || 0);
        }
        rowsByLabel.set(label, row);
      }
    }

    return {
      buckets: Array.from(rowsByLabel.values()),
      keys,
    };
  }, [
    isMultiCategoryOverlayMode,
    rawTimeSeries,
    rawCompareTimeSeries,
    chartCategoryFilter,
    dateRange.from,
    dateRange.to,
    compareDateRangeEffective,
  ]);

  const fmtEur = (v: number | undefined | null) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
    const n = Math.abs(v);
    const [int, dec] = n.toFixed(2).split('.');
    const withDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return (v < 0 ? '−' : '') + withDots + ',' + dec + ' €';
  };

  const CATEGORY_LINE_COLORS = [
    palette.primary.main,
    '#f97316', // orange
    '#0ea5e9', // sky
    '#22c55e', // green
    '#a855f7', // purple
  ] as const;

  const renderMultiCategoryChart = (
    buckets: Array<{ label: string } & Record<string, number>>,
    keys: Array<{ mainKey: string; compareKey: string; label: string }>,
  ) => (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={buckets}
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={palette.grey[300]} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 12 }}
          stroke={palette.grey[600]}
        />
        <YAxis
          yAxisId="revenue"
          orientation="left"
          tick={{ fontSize: 11 }}
          stroke={palette.grey[600]}
          tickFormatter={(v) =>
            v >= 1000 ? `${(v / 1000).toFixed(0)}k €` : `${v} €`
          }
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const ordered = [...payload]
              .filter((p) => p && p.value != null)
              .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
            return (
              <Box
                sx={{
                  bgcolor: palette.common.white,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: palette.grey[300],
                  boxShadow: `0 8px 24px ${palette.grey[300]}40, 0 0 0 1px ${palette.primary.main}20`,
                  p: 1.5,
                  minWidth: 220,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: palette.text.primary,
                    fontWeight: 700,
                    mb: 1,
                    pb: 0.75,
                    borderBottom: '1px solid',
                    borderColor: palette.grey[200],
                  }}
                >
                  {label}
                </Typography>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {ordered.map((p: any) => (
                    <Box
                      key={String(p.dataKey)}
                      component="li"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.35,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: p.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: palette.text.secondary,
                          fontSize: '0.8125rem',
                          flex: 1,
                        }}
                      >
                        {String(p.name || p.dataKey)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: palette.text.primary,
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                        }}
                      >
                        {fmtEur(Number(p.value))}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          }}
        />
        <Legend />
        {keys.map((k, i) => (
          <Line
            key={k.mainKey}
            yAxisId="revenue"
            type="monotone"
            dataKey={k.mainKey}
            name={k.label}
            stroke={CATEGORY_LINE_COLORS[i % CATEGORY_LINE_COLORS.length]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls
          />
        ))}
        {compareDateRangeEffective &&
          keys.map((k, i) => (
            <Line
              key={k.compareKey}
              yAxisId="revenue"
              type="monotone"
              dataKey={k.compareKey}
              name={`${k.label} (compare)`}
              stroke={CATEGORY_LINE_COLORS[i % CATEGORY_LINE_COLORS.length]}
              strokeOpacity={0.45}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              dot={false}
              activeDot={{ r: 3 }}
              connectNulls
            />
          ))}
      </LineChart>
    </ResponsiveContainer>
  );

  const renderChart = (buckets: ChartBucket[], showLegend: boolean) => (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={buckets}
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={palette.grey[300]} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: showLegend ? 12 : 11 }}
          stroke={palette.grey[600]}
        />
        <YAxis
          yAxisId="revenue"
          orientation="left"
          tick={{ fontSize: showLegend ? 11 : 10 }}
          stroke={palette.grey[600]}
          tickFormatter={(v) =>
            v >= 1000 ? `${(v / 1000).toFixed(0)}k €` : `${v} €`
          }
        />
        <YAxis
          yAxisId="quantity"
          orientation="right"
          tick={{ fontSize: showLegend ? 11 : 10 }}
          stroke={palette.grey[600]}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const byName = Object.fromEntries(payload.map((p) => [p.name, p]));
            const ordered = TOOLTIP_ORDER.filter((name) => byName[name]).map(
              (name) => ({
                name,
                value: byName[name].value,
                label: TOOLTIP_LABELS[name],
                color: TOOLTIP_COLORS[name] ?? byName[name].color,
              }),
            );
            return (
              <Box
                sx={{
                  bgcolor: palette.common.white,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: palette.grey[300],
                  boxShadow: `0 8px 24px ${palette.grey[300]}40, 0 0 0 1px ${palette.primary.main}20`,
                  p: 1.5,
                  minWidth: 200,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: palette.text.primary,
                    fontWeight: 700,
                    mb: 1,
                    pb: 0.75,
                    borderBottom: '1px solid',
                    borderColor: palette.grey[200],
                  }}
                >
                  {label}
                </Typography>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {ordered.map(({ name, value, label: rowLabel, color }) => (
                    <Box
                      key={name}
                      component="li"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.35,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: palette.text.secondary,
                          fontSize: '0.8125rem',
                          flex: 1,
                        }}
                      >
                        {rowLabel}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: palette.text.primary,
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                        }}
                      >
                        {name === 'revenue' || name === 'revenueCompare'
                          ? fmtEur(value)
                          : Number(value).toLocaleString('it-IT')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          }}
        />
        {showLegend && (
          <Legend
            formatter={(value) =>
              value === 'revenue'
                ? 'Revenue (main)'
                : value === 'revenueCompare'
                  ? 'Revenue (compare)'
                  : value === 'quantity'
                    ? 'Quantity (main)'
                    : 'Quantity (compare)'
            }
            itemSorter={(item) => {
              const order = [
                'revenue',
                'revenueCompare',
                'quantity',
                'quantityCompare',
              ];
              const key = String(item.dataKey ?? item.value ?? '');
              const idx = order.indexOf(key);
              return idx === -1 ? 999 : idx;
            }}
          />
        )}
        {/* Ordine di disegno: Quantity prima (sotto), Revenue dopo (sopra). */}
        <Line
          yAxisId="quantity"
          type="monotone"
          dataKey="quantity"
          name="quantity"
          stroke={palette.grey[600]}
          strokeWidth={CHART_MAIN.strokeWidth}
          dot={{ r: CHART_MAIN.dotRadius }}
          activeDot={{ r: CHART_MAIN.activeDotRadius }}
        />
        {compareDateRangeEffective &&
          buckets.some((b) => b.quantityCompare != null) && (
            <Line
              yAxisId="quantity"
              type="monotone"
              dataKey="quantityCompare"
              name="quantityCompare"
              stroke={CHART_COMPARE.quantityStroke}
              strokeWidth={CHART_COMPARE.strokeWidth}
              strokeDasharray={CHART_COMPARE.strokeDasharray}
              dot={CHART_COMPARE.dot}
              activeDot={{ r: CHART_COMPARE.activeDotRadius }}
            />
          )}
        <Line
          yAxisId="revenue"
          type="monotone"
          dataKey="revenue"
          name="revenue"
          stroke={palette.primary.main}
          strokeWidth={CHART_MAIN.strokeWidth}
          dot={{ r: CHART_MAIN.dotRadius }}
          activeDot={{ r: CHART_MAIN.activeDotRadius }}
        />
        {compareDateRangeEffective &&
          buckets.some((b) => b.revenueCompare != null) && (
            <Line
              yAxisId="revenue"
              type="monotone"
              dataKey="revenueCompare"
              name="revenueCompare"
              stroke={CHART_COMPARE.revenueStroke}
              strokeWidth={CHART_COMPARE.strokeWidth}
              strokeDasharray={CHART_COMPARE.strokeDasharray}
              dot={CHART_COMPARE.dot}
              activeDot={{ r: CHART_COMPARE.activeDotRadius }}
            />
          )}
      </LineChart>
    </ResponsiveContainer>
  );

  if (error) {
    return (
      <PageContainer sx={containerSx}>
        <ContentContainer>
          <Typography color="error">{error.message}</Typography>
        </ContentContainer>
      </PageContainer>
    );
  }

  const periodLabel =
    dateRangePreset === 'custom'
      ? `${dateRange.from} → ${dateRange.to}`
      : `Last ${dateRangePreset} days`;

  /** Etichetta periodo di confronto: breve e leggibile, coerente con il select Compare. */
  const comparePeriodLabel = (() => {
    if (!compareDateRangeEffective) return '';
    if (compareMode === 'previous_period') return 'Previous period';
    if (compareMode === 'previous_year') return 'Previous year';
    if (
      compareMode === 'custom' &&
      compareDateRangeEffective.from &&
      compareDateRangeEffective.to
    ) {
      const from = format(parseISO(compareDateRangeEffective.from), 'd MMM');
      const to = format(parseISO(compareDateRangeEffective.to), 'd MMM');
      return `${from} – ${to}`;
    }
    return `${compareDateRangeEffective.from} – ${compareDateRangeEffective.to}`;
  })();

  const controlHeight = 40;
  const controlRadius = 2;
  const controlSx = {
    height: controlHeight,
    minHeight: controlHeight,
    borderRadius: controlRadius,
    backgroundColor: 'white',
    '& .MuiOutlinedInput-root': {
      height: controlHeight,
      minHeight: controlHeight,
      borderRadius: controlRadius,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.primary.main },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.primary.main,
    },
  };
  const labelSx = {
    display: 'block',
    mb: 0.5,
    fontSize: '0.7rem',
    fontWeight: 600,
    color: palette.grey[500],
  };

  return (
    <PageContainer sx={containerSx}>
      <ContentContainer>
        <Box
          sx={{
            height: 4,
            borderRadius: '0 0 4px 4px',
            background: `linear-gradient(90deg, ${palette.primary.main}, ${palette.primary.light})`,
            mb: 2,
            mx: -0.5,
          }}
        />

        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: palette.grey[200],
            background: `linear-gradient(180deg, ${palette.primary.main}05 0%, ${palette.primary.main}02 25%, transparent 70%)`,
            mt: -0.5,
            pt: 2,
            px: 2,
            mx: -2,
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${palette.primary.main}18, transparent)`,
              opacity: 0.6,
            },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: `${palette.primary.main}18`,
              border: '1px solid',
              borderColor: `${palette.primary.main}35`,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: palette.primary.main,
                flexShrink: 0,
                boxShadow: `0 0 0 2px ${palette.primary.main}40`,
              }}
            />
            <Typography
              sx={{
                color: palette.primary.main,
                fontWeight: 700,
                letterSpacing: 1.1,
                fontSize: '0.7rem',
              }}
            >
              CATEGORY ANALYSIS
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Typography
              variant="h4"
              sx={{
                color: palette.grey[800],
                fontWeight: 800,
                letterSpacing: '-0.025em',
                lineHeight: 1.2,
              }}
            >
              Revenue and units sold over time
            </Typography>
            <Box
              sx={{
                width: '60%',
                height: 3,
                borderRadius: '0 0 2px 2px',
                background: `linear-gradient(90deg, ${palette.primary.main}, transparent)`,
                opacity: 0.5,
                mt: 0.5,
              }}
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 0, maxWidth: 480 }}
          >
            Compare revenue and quantity sold by period. Filter by category to
            focus on specific collections.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.5,
            mb: 1.5,
            flexWrap: 'wrap',
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid',
            borderColor: palette.grey[200],
          }}
        >
          <Box>
            <Typography component="label" sx={labelSx}>
              Period
            </Typography>
            <FormControl size="small" sx={{ width: 145, ...controlSx }}>
              <Select
                value={dateRangePreset}
                onChange={(e) => {
                  const preset = e.target.value;
                  if (preset !== 'custom') {
                    const days = parseInt(preset, 10);
                    if (!isNaN(days) && !canUsePresetDays(days)) {
                      snackbar(
                        getLookbackPresetBlockedMessage(lookbackDays),
                        'warning',
                      );
                      return;
                    }
                  }
                  setDateRangePreset(preset);
                  setShowCustomDateRange(preset === 'custom');
                  setTempCustomDates(null);
                  if (preset !== 'custom') {
                    const days = parseInt(preset, 10);
                    const newRange = getLastNDaysDateRange(days);
                    setDateRange(newRange);
                    if (compareMode === 'previous_period')
                      setCompareDateRange(getPreviousPeriodDateRange(newRange));
                    else if (compareMode === 'previous_year')
                      setCompareDateRange(getPreviousYearDateRange(newRange));
                  } else {
                    setTempCustomDates({
                      from: dateRange.from,
                      to: dateRange.to,
                    });
                  }
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0,
                    height: controlHeight - 2,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="7" disabled={!canUsePresetDays(7)}>
                  Last 7 days
                </MenuItem>
                <MenuItem value="30" disabled={!canUsePresetDays(30)}>
                  Last 30 days
                </MenuItem>
                <MenuItem value="90" disabled={!canUsePresetDays(90)}>
                  Last 90 days
                </MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {showCustomDateRange && (
            <>
              <TextField
                type="date"
                label="From"
                size="small"
                value={tempCustomDates?.from ?? dateRange.from}
                onChange={(e) => {
                  const v = e.target.value;
                  const t = tempCustomDates?.to ?? dateRange.to;
                  setTempCustomDates(
                    t && v > t
                      ? { from: v, to: v }
                      : { from: v, to: tempCustomDates?.to ?? dateRange.to },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: lookbackMinDate || undefined,
                  max: (tempCustomDates?.to ?? dateRange.to) || undefined,
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={tempCustomDates?.to ?? dateRange.to}
                onChange={(e) => {
                  const v = e.target.value;
                  const f = tempCustomDates?.from ?? dateRange.from;
                  setTempCustomDates(
                    f && v < f
                      ? { from: v, to: v }
                      : {
                          from: tempCustomDates?.from ?? dateRange.from,
                          to: v,
                        },
                  );
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: (tempCustomDates?.from ?? dateRange.from) || undefined,
                  max: new Date().toISOString().split('T')[0],
                }}
                sx={{ width: 140, ...controlSx }}
              />
              <Button
                variant="contained"
                size="small"
                className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                onClick={() => {
                  if (
                    tempCustomDates?.from &&
                    tempCustomDates?.to &&
                    tempCustomDates.from <= tempCustomDates.to
                  ) {
                    if (!isRangeWithinLookback(tempCustomDates)) {
                      snackbar(
                        getLookbackCustomRangeBlockedMessage(
                          lookbackDays,
                          'date',
                        ),
                        'warning',
                      );
                      return;
                    }
                    setDateRange(tempCustomDates);
                    setTempCustomDates(null);
                  }
                }}
                disabled={
                  !tempCustomDates?.from ||
                  !tempCustomDates?.to ||
                  tempCustomDates.from > tempCustomDates.to
                }
                sx={{
                  minHeight: controlHeight,
                  borderRadius: controlRadius,
                  bgcolor: palette.primary.main,
                  color: palette.primary.contrastText,
                  minWidth: 60,
                }}
              >
                Apply
              </Button>
            </>
          )}

          <Box>
            <Typography component="label" sx={labelSx}>
              Compare
            </Typography>
            <FormControl size="small" sx={{ width: 155, ...controlSx }}>
              <Select
                value={compareMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  if (
                    mode === 'previous_period' &&
                    !canUsePreviousPeriodCompare
                  ) {
                    snackbar(
                      getLookbackCompareBlockedMessage(
                        'previous_period',
                        lookbackDays,
                      ),
                      'warning',
                    );
                    return;
                  }
                  if (mode === 'previous_year' && !canUsePreviousYearCompare) {
                    snackbar(
                      getLookbackCompareBlockedMessage(
                        'previous_year',
                        lookbackDays,
                      ),
                      'warning',
                    );
                    return;
                  }
                  setCompareMode(mode);
                  if (mode === 'none') {
                    setCompareDateRange(null);
                    setShowCustomCompareDates(false);
                    setTempCustomCompareDates(null);
                  } else if (mode === 'previous_period') {
                    setCompareDateRange(getPreviousPeriodDateRange(dateRange));
                    setShowCustomCompareDates(false);
                  } else if (mode === 'previous_year') {
                    setCompareDateRange(getPreviousYearDateRange(dateRange));
                    setShowCustomCompareDates(false);
                  } else if (mode === 'custom') {
                    setShowCustomCompareDates(true);
                    setTempCustomCompareDates(
                      compareDateRange || {
                        from: dateRange.from,
                        to: dateRange.to,
                      },
                    );
                  }
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0,
                    height: controlHeight - 2,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem
                  value="previous_period"
                  disabled={!canUsePreviousPeriodCompare}
                >
                  Previous period
                </MenuItem>
                <MenuItem
                  value="previous_year"
                  disabled={!canUsePreviousYearCompare}
                >
                  Previous year
                </MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {lookbackDays !== null && lookbackDays !== undefined && (
            <Typography
              variant="caption"
              sx={{
                width: '100%',
                color: 'warning.main',
                fontWeight: 500,
                mt: 0.5,
              }}
            >
              {getLookbackPlanLimitCaption(lookbackDays)}
            </Typography>
          )}

          <Box
            sx={{
              flex: '1 1 auto',
              minWidth: { xs: '100%', sm: 220 },
              maxWidth: { sm: 320 },
            }}
          >
            <Typography component="label" sx={labelSx}>
              Category (chart)
            </Typography>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              options={availableCollections}
              value={chartCategoryFilter}
              onChange={(_, v) => setChartCategoryFilter(v.slice(0, 5))}
              renderOption={(props, o, { selected }) => (
                <li {...props}>
                  <Checkbox
                    checked={selected}
                    sx={{ mr: 0.5, p: 0 }}
                    size="small"
                  />
                  <Typography variant="body2">{o}</Typography>
                </li>
              )}
              renderInput={(p) => (
                <TextField
                  {...p}
                  placeholder={
                    chartCategoryFilter.length
                      ? `${chartCategoryFilter.length} selected`
                      : 'All categories'
                  }
                  sx={{
                    ...controlSx,
                    '& .MuiInputBase-root': { minHeight: controlHeight },
                  }}
                />
              )}
              renderTags={() => null}
            />
          </Box>

          <Box>
            <Typography component="label" sx={labelSx}>
              Filters
            </Typography>
            <Button
              size="small"
              variant={showMoreFilters ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    fontSize: 16,
                    transform: showMoreFilters ? 'rotate(180deg)' : 'none',
                  }}
                />
              }
              onClick={() => setShowMoreFilters((v) => !v)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                height: controlHeight,
                minHeight: controlHeight,
                boxSizing: 'border-box',
                borderRadius: controlRadius,
                px: 1.5,
                py: 0,
                whiteSpace: 'nowrap',
                fontSize: '0.8125rem',
                borderColor:
                  activeFilterCount > 0 ? palette.primary.main : '#e0e0e0',
                color: showMoreFilters ? '#fff' : 'text.secondary',
                bgcolor: showMoreFilters ? palette.primary.main : 'white',
                '&:hover': {
                  bgcolor: showMoreFilters
                    ? palette.primary.light
                    : 'rgba(0,0,0,0.04)',
                  borderColor: palette.primary.main,
                },
              }}
            >
              Filters{' '}
              {activeFilterCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: showMoreFilters
                      ? 'rgba(255,255,255,0.3)'
                      : palette.primary.main,
                    color: '#fff',
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Button>
          </Box>
        </Box>

        {chartCategoryFilter.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, mr: 0.5 }}
            >
              Chart categories:
            </Typography>
            {chartCategoryFilter.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                onDelete={() =>
                  setChartCategoryFilter((p) => p.filter((x) => x !== c))
                }
                sx={{
                  borderRadius: 1,
                  fontSize: 12,
                  borderColor: palette.primary.main,
                  borderWidth: 1,
                  borderStyle: 'solid',
                  bgcolor: `${palette.primary.main}18`,
                  color: palette.primary.main,
                }}
              />
            ))}
          </Box>
        )}

        {activeFilterCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {countryFilter.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                onDelete={() =>
                  setCountryFilter((p) => p.filter((x) => x !== c))
                }
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            {customerTypeFilter && (
              <Chip
                size="small"
                label={
                  customerTypeFilter === 'First time' ? 'New' : 'Recurring'
                }
                onDelete={() => setCustomerTypeFilter('')}
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            )}
            {tagFilter.map((t) => (
              <Chip
                key={t}
                size="small"
                label={t}
                onDelete={() => setTagFilter((p) => p.filter((x) => x !== t))}
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            {collectionFilter.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                onDelete={() =>
                  setCollectionFilter((p) => p.filter((x) => x !== c))
                }
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            {vendorFilter.map((v) => (
              <Chip
                key={v}
                size="small"
                label={v}
                onDelete={() =>
                  setVendorFilter((p) => p.filter((x) => x !== v))
                }
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            {productTypeFilter.map((p) => (
              <Chip
                key={p}
                size="small"
                label={p}
                onDelete={() =>
                  setProductTypeFilter((prev) => prev.filter((x) => x !== p))
                }
                sx={{ borderRadius: 1, fontSize: 12 }}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setCountryFilter([]);
                setCustomerTypeFilter('');
                setTagFilter([]);
                setCollectionFilter([]);
                setVendorFilter([]);
                setProductTypeFilter([]);
              }}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                color: 'text.secondary',
              }}
            >
              Clear all
            </Button>
          </Box>
        )}

        <Collapse in={showMoreFilters} timeout={200}>
          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 1.5,
              mb: 1.5,
              p: 2,
              borderRadius: 2,
              backgroundColor: palette.background.tableRow,
              border: '1px solid',
              borderColor: palette.grey[300],
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Country
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={FILTER_COUNTRY_OPTIONS}
                value={countryFilter}
                onChange={(_, v) => setCountryFilter(v)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{option}</Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select countries..."
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Customer type
              </Typography>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={customerTypeFilter}
                onChange={(e) => setCustomerTypeFilter(e.target.value)}
                sx={{ backgroundColor: 'white' }}
                renderValue={(v) =>
                  v
                    ? v === 'First time'
                      ? 'New'
                      : 'Recurring'
                    : 'All customers'
                }
              >
                <MenuItem value="">All customers</MenuItem>
                <MenuItem value="First time">New (first time)</MenuItem>
                <MenuItem value="recurring">Recurring</MenuItem>
              </Select>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Tag
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableTags}
                value={tagFilter}
                onChange={(_, v) => setTagFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select tags..."
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Collection
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableCollections}
                value={collectionFilter}
                onChange={(_, v) => setCollectionFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select collections..."
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Vendor
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableVendors}
                value={vendorFilter}
                onChange={(_, v) => setVendorFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select vendors..."
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
              >
                Product Type
              </Typography>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                options={availableProductTypes}
                value={productTypeFilter}
                onChange={(_, v) => setProductTypeFilter(v)}
                renderOption={(props, o, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 0.5, p: 0 }}
                      size="small"
                    />
                    <Typography variant="body2">{o}</Typography>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    placeholder="Select types..."
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: 'white' },
                    }}
                  />
                )}
                renderTags={() => null}
              />
            </Box>
          </Box>
        </Collapse>

        {loading || timeSeriesLoading ? (
          <Card
            sx={{
              mt: 1,
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow:
                '0 4px 24px -4px rgba(0,0,0,0.08), 0 2px 8px -2px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent sx={{ pt: 3, pb: 3, px: 2.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, textAlign: 'center', fontWeight: 500 }}
              >
                Preparing your category chart…
              </Typography>
              <Box
                sx={{
                  height: 200,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 0.5,
                  justifyContent: 'center',
                }}
              >
                {Array.from({ length: 48 }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 4,
                      minWidth: 4,
                      height: `${28 + Math.sin(i * 0.5) * 30 + (i % 7) * 8}%`,
                      maxHeight: '95%',
                      borderRadius: '2px 2px 0 0',
                      bgcolor: palette.primary.main,
                      animation: `${chartLoadPulseKeyframes} 1.8s ease-in-out infinite`,
                      animationDelay: `${(i % 12) * 0.08}s`,
                      transformOrigin: 'bottom',
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        ) : !isMultiCategoryOverlayMode && chartBuckets.length === 0 ? (
          <Card sx={{ mt: 1, borderRadius: 2 }}>
            <CardContent sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No data for the selected period and filters. Try a wider date
                range or different category.
              </Typography>
            </CardContent>
          </Card>
        ) : isMultiCategoryOverlayMode &&
          multiCategorySeries.buckets.length === 0 ? (
          <Card sx={{ mt: 1, borderRadius: 2 }}>
            <CardContent sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No time-series data for the selected categories in this period.
                Try a wider date range or different categories.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card
              sx={{
                mt: 1,
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow:
                  '0 4px 24px -4px rgba(0,0,0,0.08), 0 2px 8px -2px rgba(0,0,0,0.04)',
              }}
            >
              <CardContent sx={{ pt: 2, pb: 2, px: 2.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5, fontWeight: 500 }}
                >
                  {periodLabel}
                  {comparePeriodLabel ? ` vs ${comparePeriodLabel}` : ''}
                  {' · '}
                  {chartDataProducts.length} products
                  {' · '}
                  {chartCategoryFilter.length > 0
                    ? `${chartCategoryFilter.length} categor${chartCategoryFilter.length === 1 ? 'y' : 'ies'}`
                    : 'All categories'}
                  {' · '}
                  {(isMultiCategoryOverlayMode
                    ? multiCategorySeries.buckets.length
                    : chartBuckets.length) >= 60
                    ? 'By month'
                    : 'By day'}
                  {isMultiCategoryOverlayMode ? ' · Revenue by category' : ''}
                </Typography>
                <Box sx={{ width: '100%', height: 340 }}>
                  {isMultiCategoryOverlayMode
                    ? renderMultiCategoryChart(
                        multiCategorySeries.buckets,
                        multiCategorySeries.keys,
                      )
                    : renderChart(chartBuckets, true)}
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default CategoryAnalysis;
